import { useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Dialog } from 'primereact/dialog';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import { GLOBAL_DASHBOARD_ID } from '../store/CapacityPlannerStore';
import { toast } from 'react-toastify';

interface Props {
  visible: boolean;
  onHide: () => void;
}

const CPVersionHistory = observer(({ visible, onHide }: Props) => {
  const store = useCapacityStore();
  const [search, setSearch] = useState('');
  const [settingActive, setSettingActive] = useState<string | null>(null);

  const record = store.dashboardRecords.find((d) => d.id === GLOBAL_DASHBOARD_ID);
  if (!record) return null;

  const activeVersionId  = record.activeVersionId;
  const viewingVersionId = store.activeDashboardId === GLOBAL_DASHBOARD_ID
    ? store.activeDashboardVersionId
    : null;

  // Reverse so newest is first; apply search filter
  const filteredVersions = useMemo(() => {
    const q = search.toLowerCase().trim();
    return [...record.versions]
      .reverse()
      .filter((v) =>
        !q ||
        v.versionId.toLowerCase().includes(q) ||
        v.label.toLowerCase().includes(q) ||
        v.description.toLowerCase().includes(q) ||
        v.startYearMonth.includes(q),
      );
  }, [record.versions, search]);

  const handleView = (versionId: string) => {
    store.switchVersion(GLOBAL_DASHBOARD_ID, versionId);
    store.setActiveView('dashboard');
    onHide();
  };

  const handleSetActive = async (versionId: string) => {
    setSettingActive(versionId);
    await store.setGlobalActiveVersion(versionId);
    const ver = record.versions.find((v) => v.versionId === versionId);
    toast.success(`★ ${versionId} is now the active global version.`, { position: 'bottom-right', autoClose: 4000 });
    setSettingActive(null);
    onHide();
  };

  const totalCount = record.versions.length;

  return (
    <Dialog
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="pi pi-history" style={{ color: '#522d6e', fontSize: 18 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
              Version History
            </div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 1 }}>
              {record.name} &nbsp;·&nbsp; {totalCount} version{totalCount !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      }
      visible={visible}
      onHide={onHide}
      style={{ width: '720px' }}
      contentStyle={{ padding: 0 }}
      modal
      dismissableMask
    >
      {/* ---- Search ---- */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
        <div style={{ position: 'relative' }}>
          <i className="pi pi-search" style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: '#94a3b8', fontSize: 13,
          }} />
          <input
            type="text"
            placeholder="Search versions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '7px 12px 7px 32px',
              border: '1px solid #e2e8f0', borderRadius: 7,
              fontSize: 13, color: '#1e293b', background: '#fff',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}
            >
              <i className="pi pi-times" style={{ fontSize: 11 }} />
            </button>
          )}
        </div>
      </div>

      {/* ---- Legend ---- */}
      <div style={{
        display: 'flex', gap: 20, padding: '8px 20px',
        background: '#f8f5fb', borderBottom: '1px solid #ede3f7',
        fontSize: 11, color: '#64748b',
      }}>
        <span><span style={{ color: '#f59e0b', fontWeight: 800 }}>★</span> Active global version (shown to all users on landing)</span>
        <span><span style={{ color: '#522d6e', fontWeight: 800 }}>●</span> Currently viewing</span>
      </div>

      {/* ---- Version table ---- */}
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {/* Sticky header */}
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 110 }} />
            <col />
            <col style={{ width: 90 }} />
            <col style={{ width: 80 }} />
            <col style={{ width: 140 }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#fff', position: 'sticky', top: 0, zIndex: 1, boxShadow: '0 1px 0 #e2e8f0' }}>
              {['Version', 'Label & Description', 'Date', 'Start Mth', 'Actions'].map((h) => (
                <th key={h} style={{
                  padding: '9px 12px', textAlign: 'left',
                  fontSize: 10, fontWeight: 700, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredVersions.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: '32px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  No versions match "{search}"
                </td>
              </tr>
            ) : (
              filteredVersions.map((ver) => {
                const isActive   = ver.versionId === activeVersionId;
                const isViewing  = ver.versionId === viewingVersionId;
                const isSetting  = settingActive === ver.versionId;
                const rowBg      = isActive ? '#fffbeb' : isViewing ? '#f8f5fb' : 'transparent';

                return (
                  <tr
                    key={ver.versionId}
                    style={{ background: rowBg, borderBottom: '1px solid #f1f5f9' }}
                  >
                    {/* Version cell */}
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        {isActive && (
                          <span title="Active global version" style={{ color: '#f59e0b', fontSize: 13, lineHeight: 1 }}>★</span>
                        )}
                        {isViewing && (
                          <span title="Currently viewing" style={{ color: '#522d6e', fontSize: 13, lineHeight: 1 }}>●</span>
                        )}
                        {!isActive && !isViewing && (
                          <span style={{ display: 'inline-block', width: 13 }} />
                        )}
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          background: isActive ? '#fef3c7' : '#f1f5f9',
                          color: isActive ? '#92400e' : '#475569',
                          padding: '2px 8px', borderRadius: 8,
                          border: `1px solid ${isActive ? '#fcd34d' : '#e2e8f0'}`,
                          whiteSpace: 'nowrap',
                        }}>
                          {ver.versionId}
                        </span>
                      </div>
                    </td>

                    {/* Label & description */}
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>
                        {ver.label}
                      </div>
                      <div style={{
                        fontSize: 11, color: '#64748b', lineHeight: 1.4,
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {ver.description}
                      </div>
                    </td>

                    {/* Date */}
                    <td style={{ padding: '10px 12px', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {ver.createdAt}
                    </td>

                    {/* Start month */}
                    <td style={{ padding: '10px 12px', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {ver.startYearMonth}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {isViewing ? (
                          <span style={{ fontSize: 10, color: '#522d6e', fontWeight: 600 }}>Viewing</span>
                        ) : (
                          <button
                            onClick={() => handleView(ver.versionId)}
                            style={{
                              fontSize: 11, padding: '3px 9px', borderRadius: 6,
                              background: '#f1f5f9', color: '#475569',
                              border: '1px solid #e2e8f0', cursor: 'pointer',
                              fontWeight: 500,
                            }}
                          >
                            View
                          </button>
                        )}
                        {isActive ? (
                          <span style={{
                            fontSize: 10, padding: '3px 8px', borderRadius: 6,
                            background: '#fef3c7', color: '#92400e',
                            border: '1px solid #fcd34d', fontWeight: 700,
                          }}>
                            ★ Active
                          </span>
                        ) : (
                          <button
                            onClick={() => handleSetActive(ver.versionId)}
                            disabled={!!settingActive}
                            title="Set this as the active version shown to all users"
                            style={{
                              fontSize: 11, padding: '3px 9px', borderRadius: 6,
                              background: settingActive === ver.versionId ? '#fef3c7' : '#fff',
                              color: '#b45309',
                              border: '1px solid #fcd34d', cursor: isSetting ? 'wait' : 'pointer',
                              fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                            }}
                          >
                            {isSetting
                              ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 10 }} /> Setting…</>
                              : <>★ Set Active</>
                            }
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ---- Footer ---- */}
      <div style={{
        padding: '10px 20px', borderTop: '1px solid #f1f5f9',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#fafafa',
      }}>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          {filteredVersions.length === totalCount
            ? `${totalCount} version${totalCount !== 1 ? 's' : ''} total`
            : `Showing ${filteredVersions.length} of ${totalCount}`}
        </span>
        <button
          onClick={onHide}
          style={{
            padding: '6px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </Dialog>
  );
});

export default CPVersionHistory;
